import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus, Save } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { AuditChip } from '@/components/audit/AuditChip';
import {
  CsvPasteGrid,
  type CsvColumn,
} from '@/components/forms/CsvPasteGrid';
import { REGION_PRESETS, RegionInput } from '@/components/forms/RegionInput';
import {
  useBulkSetInventory,
  useDesigners,
  useInventory,
  useProducts,
} from '@/queries/commerce';
import { useApiError } from '@/lib/useApiError';
import {
  inventoryBulkRequestSchema,
  type InventoryBulkRequest,
  type InventoryItem,
} from '@/schemas/admin/commerce/inventory';

export const Route = createFileRoute('/_authed/commerce/inventory')({
  component: InventoryScreen,
});

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface CsvRow extends Record<string, string> {
  variant_id: string;
  region: string;
  stock_count: string;
}

const csvColumns: readonly CsvColumn<CsvRow>[] = [
  {
    id: 'variant_id',
    header: 'variant_id',
    aliases: ['variant', 'variantid'],
    width: '40%',
    validate: (value) => {
      if (!value) return 'Required';
      if (!UUID_REGEX.test(value)) return 'Not a UUID';
      return null;
    },
  },
  {
    id: 'region',
    header: 'region',
    aliases: ['country', 'zone'],
    width: '20%',
    validate: (value) => {
      if (!value) return 'Required';
      if (value.length > 10) return 'Max 10 chars';
      return null;
    },
  },
  {
    id: 'stock_count',
    header: 'stock_count',
    aliases: ['stock', 'count', 'qty', 'quantity'],
    width: '20%',
    validate: (value) => {
      if (!value) return 'Required';
      if (!/^\d+$/.test(value)) return 'Whole number';
      const n = Number(value);
      if (n < 0) return 'Cannot be negative';
      return null;
    },
  },
];

function InventoryScreen() {
  const { t } = useTranslation('commerce');
  const { t: tc } = useTranslation('common');
  const designers = useDesigners();
  const products = useProducts({ limit: 200 });
  const bulk = useBulkSetInventory();
  const resolveError = useApiError();

  const [mode, setMode] = React.useState<'grid' | 'csv'>('grid');
  const [designerId, setDesignerId] = React.useState<string>('');
  const [productId, setProductId] = React.useState<string>('');

  const filteredProducts = React.useMemo(
    () =>
      (products.data ?? []).filter(
        (p) => !p.deleted_at && (!designerId || p.designer_id === designerId)
      ),
    [products.data, designerId]
  );

  const variants = React.useMemo(() => {
    const product = filteredProducts.find((p) => p.id === productId);
    return (product?.variants ?? []).filter((v) => !v.deleted_at);
  }, [filteredProducts, productId]);

  const variantIds = React.useMemo(() => variants.map((v) => v.id), [variants]);
  const inventory = useInventory(variantIds);

  // Grid mode: variants × regions matrix.
  const [regions, setRegions] = React.useState<string[]>(() => [...REGION_PRESETS.slice(0, 3)]);
  const [newRegionDraft, setNewRegionDraft] = React.useState('');
  const [grid, setGrid] = React.useState<Record<string, Record<string, string>>>({});

  // Seed the grid whenever inventory data arrives.
  React.useEffect(() => {
    if (!inventory.data) return;
    const next: Record<string, Record<string, string>> = {};
    for (const variant of variants) {
      next[variant.id] = {};
      for (const region of regions) {
        const found = inventory.data.find(
          (row) => row.variant_id === variant.id && row.region === region
        );
        next[variant.id]![region] = found ? String(found.stock_count) : '';
      }
    }
    setGrid(next);
    // Trigger seed only when inventory or selected variants change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory.data, productId]);

  // CSV mode rows.
  const [csvRows, setCsvRows] = React.useState<CsvRow[]>([]);

  function setCell(variantId: string, region: string, value: string) {
    setGrid((prev) => ({
      ...prev,
      [variantId]: { ...(prev[variantId] ?? {}), [region]: value },
    }));
  }

  function addRegion() {
    const trimmed = newRegionDraft.trim().slice(0, 10);
    if (!trimmed) return;
    if (regions.includes(trimmed)) return;
    setRegions([...regions, trimmed]);
    setNewRegionDraft('');
  }

  function gridToItems(): InventoryItem[] {
    const out: InventoryItem[] = [];
    for (const variant of variants) {
      const row = grid[variant.id] ?? {};
      for (const region of regions) {
        const raw = (row[region] ?? '').trim();
        if (!raw) continue;
        if (!/^\d+$/.test(raw)) continue;
        out.push({
          variant_id: variant.id,
          region,
          stock_count: Number(raw),
        });
      }
    }
    return out;
  }

  function csvToItems(): InventoryItem[] {
    return csvRows
      .filter((r) => r.variant_id && r.region && r.stock_count)
      .map((r) => ({
        variant_id: r.variant_id.trim(),
        region: r.region.trim(),
        stock_count: Number(r.stock_count.trim()),
      }))
      .filter((r) => UUID_REGEX.test(r.variant_id) && r.stock_count >= 0);
  }

  const items = mode === 'grid' ? gridToItems() : csvToItems();
  const validation = inventoryBulkRequestSchema.safeParse({ items });
  const canSubmit = validation.success && items.length > 0 && !bulk.isPending;

  async function submit() {
    if (!canSubmit) return;
    try {
      const payload: InventoryBulkRequest = { items };
      await bulk.mutateAsync(payload);
      toast.success(t('inventory.saved', { count: items.length }));
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(
        resolved.message,
        resolved.recovery ? { description: resolved.recovery } : undefined
      );
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('inventory.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('inventory.subtitle')}</p>
        </div>
        <AuditChip />
      </header>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="inv-designer" className="mb-1.5 block text-sm font-medium">
                {t('inventory.designer')}
              </label>
              <select
                id="inv-designer"
                value={designerId}
                onChange={(e) => {
                  setDesignerId(e.target.value);
                  setProductId('');
                }}
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm text-[color:var(--color-fg)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value="">{t('inventory.allDesigners')}</option>
                {(designers.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name_en ?? d.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="inv-product" className="mb-1.5 block text-sm font-medium">
                {t('inventory.product')}
              </label>
              <select
                id="inv-product"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm text-[color:var(--color-fg)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value="">{t('inventory.pickProduct')}</option>
                {filteredProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name_en}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={mode} onValueChange={(v) => setMode(v as 'grid' | 'csv')}>
        <TabsList>
          <TabsTrigger value="grid">{t('inventory.modes.grid')}</TabsTrigger>
          <TabsTrigger value="csv">{t('inventory.modes.csv')}</TabsTrigger>
        </TabsList>

        <TabsContent value="grid">
          <Card>
            <CardContent className="space-y-3 pt-6">
              {variants.length === 0 ? (
                <p className="rounded-[var(--radius-md)] border border-dashed border-[color:var(--color-border)] p-6 text-center text-sm text-[color:var(--color-fg-muted)]">
                  {t('inventory.gridEmpty')}
                </p>
              ) : (
                <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[color:var(--color-border)]">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-[color:var(--color-surface-2)]">
                      <tr>
                        <th
                          scope="col"
                          className="border-b border-[color:var(--color-border)] px-3 py-2 text-start text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]"
                        >
                          {t('inventory.cols.variant')}
                        </th>
                        {regions.map((r) => (
                          <th
                            key={r}
                            scope="col"
                            className="border-b border-[color:var(--color-border)] px-3 py-2 text-end text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]"
                          >
                            {r}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((v) => (
                        <tr key={v.id}>
                          <th
                            scope="row"
                            className="border-b border-[color:var(--color-border)]/40 px-3 py-2 text-start"
                          >
                            <span className="block font-medium">{v.size_label}</span>
                            <span
                              dir="ltr"
                              className="block font-mono text-[11px] text-[color:var(--color-fg-muted)]"
                            >
                              {v.sku}
                            </span>
                          </th>
                          {regions.map((r) => {
                            const cellValue = grid[v.id]?.[r] ?? '';
                            const invalid =
                              cellValue.length > 0 && !/^\d+$/.test(cellValue);
                            const current = inventory.data?.find(
                              (row) => row.variant_id === v.id && row.region === r
                            )?.stock_count;
                            const dirty =
                              current !== undefined && cellValue !== '' && Number(cellValue) !== current;
                            return (
                              <td
                                key={r}
                                className="border-b border-[color:var(--color-border)]/40 px-2 py-1 align-top"
                              >
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  dir="ltr"
                                  value={cellValue}
                                  onChange={(e) => setCell(v.id, r, e.target.value.replace(/[^\d]/g, ''))}
                                  aria-invalid={invalid ? true : undefined}
                                  aria-label={`${v.sku} ${r}`}
                                  className={`block h-9 w-24 rounded-[var(--radius-sm)] border bg-transparent px-2 text-end font-mono text-xs text-[color:var(--color-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] ${
                                    invalid
                                      ? 'border-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)]/40'
                                      : dirty
                                        ? 'border-[color:var(--color-accent)]'
                                        : 'border-[color:var(--color-border)]'
                                  }`}
                                />
                                {current !== undefined ? (
                                  <span
                                    dir="ltr"
                                    className="mt-0.5 block text-end font-mono text-[10px] text-[color:var(--color-fg-muted)]"
                                  >
                                    {t('inventory.current', {
                                      defaultValue: 'now: {count}',
                                      count: current,
                                    })}
                                  </span>
                                ) : null}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex flex-wrap items-end gap-2">
                <div className="basis-48">
                  <label htmlFor="inv-region-draft" className="mb-1.5 block text-xs font-medium">
                    {t('inventory.addRegion')}
                  </label>
                  <RegionInput
                    id="inv-region-draft"
                    value={newRegionDraft}
                    onChange={setNewRegionDraft}
                  />
                </div>
                <Button type="button" size="sm" variant="secondary" onClick={addRegion}>
                  <Plus aria-hidden className="size-4" />
                  {t('inventory.addRegionButton')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="csv">
          <Card>
            <CardContent className="space-y-3 pt-6">
              <p className="text-xs text-[color:var(--color-fg-muted)]">{t('inventory.csv.hint')}</p>
              <CsvPasteGrid<CsvRow>
                columns={csvColumns}
                rows={csvRows}
                onChange={setCsvRows}
                disabled={bulk.isPending}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-lg)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 shadow-lg">
        <div className="text-xs">
          <span className="font-semibold text-[color:var(--color-fg)]">
            {t('inventory.pendingCount', { count: items.length })}
          </span>
          <span className="ms-2 text-[color:var(--color-fg-muted)]">{t('inventory.transactionalHint')}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => void inventory.refetch()} disabled={!productId}>
            {tc('actions.retry', { defaultValue: 'Refresh' })}
          </Button>
          <Button onClick={() => void submit()} loading={bulk.isPending} disabled={!canSubmit}>
            <Save aria-hidden className="size-4" />
            {t('inventory.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
